import React, { useState, Dispatch, SetStateAction, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import Button from "../../common/components/button";
import Div from "../../common/components/div";
import H2 from "../../common/components/h2";
import MedicareNumberDialog from "../../common/components/MedicareNumberDialog";
import MedicareStartDateDialog from "../../common/components/MedicareStartDateDialog";
import StackingButtons from "../../common/components/StackingButtons";
import { isValidMedicareNumber } from "../../common/functions/isValidMedicareNumber";
import { isValidYear } from "../../common/functions/isValidYear";
import translate from "../../common/functions/translate";
import { useConversion, useFormStart } from "../../common/hooks/use.analytics";
import { DateObject } from "../../common/objects/models/date.field.value";
import MedicareNumberSection from '../../common/components/MedicareNumberSection';
import CancelButton from "./CancelButton";
import { createOrUnlockAccountData } from "../../create-account/createAccountContext";
import { CreateAccount1Fields, SmartErrorAlert } from "./SmartErrorAlert";
import { InvalidField } from "../functions/errorAlertContent/getAlertContent";
import { AxiosResponse } from "axios";
import { CreateUnlockAccountResponse } from "../hooks/useCreateAccountApi";
import { DateFields2 } from "./DateFields2";
import MedicareCardHelpDialog from "./MedicareCardHelpDialog";
import H3 from "./h3";
import partACard from "../../common/images/MedicareCardPartA.svg";
import partBCard from "../../common/images/MedicareCardPartB.svg";
import pbidCard from "../../common/images/MedicareCard-PBID.svg";
import { ChoiceList } from "@cmsgov/ds-medicare-gov";
import { usePageTitle } from "../hooks/usePageTitle";
import { TealiumLinkData } from "../analytics";
interface MedicareInformationProps {
    title: "create_an_account" | "unlock_account";
    conversionName: "sign_up" | "unlocked_account";
    apiResponse:  AxiosResponse<CreateUnlockAccountResponse> | null
    nextCallback: () => void;
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    conversion: ReturnType<typeof useConversion>;
    setMedicareNumberRef?: (ref: HTMLInputElement) => void;
    setMonthRef?: (ref: HTMLInputElement) => void;
    medicareNumberRef?: HTMLElement | null;
    monthRef?: HTMLElement | null;
}

type FieldErrState = 'empty' | 'invalid' | null;

type PageView = 'parta' | 'partBSelection' | 'partb' | 'pbid';

const MedicareInformation = ({
    title,
    conversionName,
    nextCallback,
    isLoading,
    setIsLoading,
    conversion,
    apiResponse
}: MedicareInformationProps) => {
    const [medicareNumberDialogIsOpen, setMedicareNumberIsOpen,] = useState(false);
    const [medicareStartDateDialogIsOpen, setMedicareStartDateDialogIsOpen] = useState(false);
    const [mbiErrState, setMbiErrState] = useState<FieldErrState>(null);
    const [coverageDateErrState, setCoverageDateErrState] = useState<FieldErrState>(null);
    const [medicareNumberRef, setMedicareNumberRef] = useState<HTMLInputElement | null>(null);
    const [monthRef, setMonthRef] = useState<HTMLInputElement | null>(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [mbiTouched, setMbiTouched] = useState(false);
    const [dateTouched, setDateTouched] = useState(false);
    const [invalidFields, setInvalidFields] = useState<InvalidField<CreateAccount1Fields>[]>([]);
    const [pageView, setPageView] = useState<PageView>('parta');
    const { data, updateData } = useContext(createOrUnlockAccountData);
    const { medicareNumber, coverageStartDate, part } = data;

    const titleRefPartA = useRef<HTMLDivElement | null>(null);
    const titleRefPartB = useRef<HTMLDivElement | null>(null);

    const pageTitle = () => {
        return pageView === 'parta' ? translate(`${title}_part_a`) : translate(`${title}_part_b`);
    }
    usePageTitle(pageTitle());

    const formStart = useFormStart(conversionName);
    const history = useHistory();

    useEffect(() => {
        titleRefPartA.current && titleRefPartA.current.focus();
        titleRefPartB.current && titleRefPartB.current.focus();
    }, [pageView]);

    useEffect(() => {
        const isValid = isValidMedicareNumber(medicareNumber);
        const isEmpty = !medicareNumber;
        if (isValid) {
            setMbiErrState(null);
        } else if (isEmpty) {
            setMbiErrState('empty');
        } else {
            setMbiErrState('invalid');
        }
    }, [medicareNumber]);

    function updateMedicareNumber(value: string) {
        formStart();
        updateData({ medicareNumber: formatMbi(value) });
    }

    function formatMbi(medicareNumber: string) {
        return medicareNumber.split('-').join('').trim();
    }

    function getMedicareErrorMessage() {
        if (!mbiTouched && !submitAttempted) {
            return null;
        }
        if (mbiErrState === 'empty') {
            return translate('enter_your_medicare_number');
        }
        if (mbiErrState === 'invalid') {
            return translate('check_mbi_and_try_again')
        }
        return null;
    }

    useEffect(() => {
        const { month, year } = coverageStartDate;
        const isEmpty = !month && !year;
        const isInvalid = !isValidYear(year);

        if (isEmpty) {
            setCoverageDateErrState('empty');
        } else if (isInvalid) {
            setCoverageDateErrState('invalid');
        } else {
            setCoverageDateErrState(null);
        }

    }, [coverageStartDate]);

    function updateCoverageStartDate(dateObject: DateObject) {
        formStart();
        updateData({ coverageStartDate: dateObject });
    }

    function getDateFieldErrorMessage() {
        if (!dateTouched && !submitAttempted) {
            return '';
        }
        if (coverageDateErrState === 'empty') {
            return translate('enter_date_your_coverage_started');
        }
        if (coverageDateErrState === 'invalid') {
            return translate('check_medicare_cover_start_date')
        }

        return '';
    }

    function updatePart() {
        formStart();
        const newPart = part === "A" ? "B" : "A";
        updateData({ part: newPart });
        if (part === 'A') {
            setPageView('partBSelection');
            return;
        }
        setPageView('parta');
    }

    function nextButtonClick(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (isLoading) {
            return;
        }
        const partToTealiumPart: Record<PageView, TealiumLinkData['coverage_type'] | undefined> = {
            parta: 'Part A',
            partb: 'Part B',
            pbid: 'Part B: PBID',
            partBSelection: undefined
        }
        const coveragePart = partToTealiumPart[pageView];
        updateData({ analyticsPart: coveragePart });

        conversion.attempt(coveragePart);
        setIsLoading(true);
        setSubmitAttempted(true);

        const newInvalidFields: InvalidField<CreateAccount1Fields>[] = [];
        if (mbiErrState) {
            newInvalidFields.push({ name: 'mbi', type: mbiErrState });
        }
        if (coverageDateErrState) {
            newInvalidFields.push({ name: 'coverageStartDate', type: coverageDateErrState });
        }
        setInvalidFields(newInvalidFields);

        if (newInvalidFields.length) {
            setIsLoading(false);
            return;
        }

        nextCallback();
    }

    const onInvalidFields = (fields: InvalidField<CreateAccount1Fields>[]) => {
        if (fields.some(x => x.name === 'mbi')) {
            setMbiErrState('invalid');
        }
        if (fields.some(x => x.name === 'coverageStartDate')) {
            setCoverageDateErrState('invalid');
        }
    }
    const headingTitle = title === 'create_an_account' ? translate('create_an_account') : translate('unlock_account')

    function updateOption(option: 'partb' | 'pbid') {
        setPageView(option);
    }

    function getCard() {
        const cardLookup: Record<PageView, string> = {
            parta: partACard,
            partb: partBCard,
            pbid: pbidCard,
            partBSelection: ''
        }
        return cardLookup[pageView];
    }

    const toggleHeading = pageView === "parta" ? translate('do_not_have_parta') : translate('do_not_have_partb');
    const toggleButtonText = pageView === "parta" ? translate('switch_parta') : translate('switch_partb');
    const dateHeading = pageView !== 'partBSelection' && translate(`coverage_start_date_${pageView}`);

    return (
        <>
            <MedicareNumberDialog
                isOpen={medicareNumberDialogIsOpen}
                onExit={() => setMedicareNumberIsOpen(false)}
            />
            <MedicareStartDateDialog
                isOpen={medicareStartDateDialogIsOpen}
                onExit={() => setMedicareStartDateDialogIsOpen(false)}
                part={part}
            />
            <form onSubmit={nextButtonClick} method="POST">
                <fieldset
                    style={{
                        border: 0,
                        margin: 0,
                        padding: 0,
                    }}
                >
                    <Div isRow>
                        <Div cols={12}>
                            <legend>
                                <H2 className={'ds-u-margin-top--1 ds-u-margin-bottom--3'}>
                                    {translate('enter_your_mbi')}
                                </H2>
                            </legend>
                            <Div className="ds-u-margin-bottom--1">
                                {translate('to_get_started')}
                            </Div>
                            <MedicareCardHelpDialog />
                            <SmartErrorAlert
                                name={title === 'create_an_account' ? 'CreateAccount1' : 'UnlockAccount1'}
                                mbiRef={medicareNumberRef}
                                coverageStartDateRef={monthRef}
                                apiResponse={apiResponse}
                                conversion={conversion}
                                invalidFields={invalidFields}
                                onInvalidField={onInvalidFields}
                                parentHeading={headingTitle}
                            />
                        </Div>
                    </Div>
                    {pageView !== 'parta' && <>
                        {/* For screen readers */}
                        <div className={'ds-u-visibility--screen-reader'}  aria-live="assertive" tabIndex={-1} ref={titleRefPartB}>
                            {translate(`${title}_part_b`)}
                        </div>
                        <Div isRow>
                            <Div cols={12}>
                                <ChoiceList
                                    label={<h3 className={"ds-text-heading--xl"} style={{ textTransform: 'none' }}>
                                        {translate(`select_coverage`)}
                                    </h3>}
                                    labelClassName={"ds-u-measure--wide"}
                                    name={'selectCoverage'}
                                    onChange={(e) => updateOption(e.target.value)}
                                    type={'radio'}
                                    choices={[
                                        {
                                            label: translate('part_b'),
                                            value: `partb`
                                        },
                                        {
                                            label: translate('part_pbid'),
                                            value: 'pbid'
                                        }
                                    ]}
                                />
                            </Div>
                        </Div>
                    </>}
                    {pageView !== "partBSelection" && <>
                        {/* For screen readers */}
                        <div className={'ds-u-visibility--screen-reader'} aria-live="assertive" tabIndex={-1} ref={titleRefPartA}>
                            {translate(`${title}_part_a`)}
                        </div>
                        <Div isRow>
                            <Div cols={12}>
                                <H3 className="ds-u-margin-bottom--2 ds-u-margin-top--3">{translate('enter_mbi_and_start_date')}</H3>
                                {/* custom css to account for surrounding margin built into svg */}
                                <Div style={{position: 'relative', left: '-9px', top: '-9px'}}>
                                    <img
                                        alt={translate(`picture_of_card_${pageView}`)}
                                        src={getCard()}
                                        className="create-account-image"
                                    />
                                </Div>
                            </Div>
                        </Div>
                        <Div isRow>
                            <Div cols={12} colsMD={8}>
                                <MedicareNumberSection
                                    errorMessage={getMedicareErrorMessage()}
                                    inputRef={setMedicareNumberRef}
                                    onBlur={() => setMbiTouched(true)}
                                    onChange={updateMedicareNumber}
                                    onClick={formStart}
                                    onFocus={formStart}
                                    value={medicareNumber}
                                    noHint={true}
                                    labelClassName={'ds-u-margin-top--1'}
                                    parentComponentHeading={headingTitle}
                                />
                            </Div>
                        </Div>
                        <Div isRow>
                            <Div cols={12}>
                                <div style={{
                                    position: 'relative',
                                    top: 24,
                                }}>
                                    <p className={'ds-u-color--base ds-u-margin-bottom--1 ds-text-heading--sm'}>
                                        {dateHeading && dateHeading.toUpperCase()}
                                    </p>
                                </div>
                                <DateFields2
                                    date={coverageStartDate}
                                    hideDayField
                                    hint={translate('use_format_mmyyyy')}
                                    onChange={updateCoverageStartDate}
                                    onComponentBlur={() => setDateTouched(true)}
                                    monthFieldRef={setMonthRef}
                                    errorMessage={getDateFieldErrorMessage()}
                                />
                            </Div>
                        </Div>
                    </>}
                    <Div isRow>
                        <Div
                            cols={12}
                            marginTop={3}
                        >
                            <h3 className={"ds-text-heading--md ds-u-margin-bottom--1"}>
                                {toggleHeading}
                            </h3>
                            <Button
                                variation={'ghost'}
                                analyticsData={{
                                    text: toggleButtonText,
                                    linkUrl: toggleButtonText,
                                    parentComponentHeading: headingTitle
                                }}
                                onClick={updatePart}
                                style={{padding: 0}}
                            >
                                {toggleButtonText}
                            </Button>
                        </Div>
                    </Div>
                </fieldset>
                {pageView !== "partBSelection" &&
                    <Div className="button-stack">
                        <StackingButtons
                            button1={
                                <CancelButton
                                    onClick={() => history.push("/login")}
                                />
                            }
                            button2={
                                <Button
                                    variation={'solid'}
                                    mfaAnalyticsData={{
                                        text: translate('next'),
                                        parentComponentHeading: headingTitle
                                    }}
                                    type={'submit'}
                                    isLoading={isLoading}
                                >
                                    {translate('next')}
                                </Button>
                            }
                        />
                    </Div>}
            </form>
        </>
    );
};

export default MedicareInformation;
